<template>
  <div class="event-bubble">
    <h2>Details & Anmeldung</h2>

    <template v-if="nextEvent">
      <!-- FALL 1: Der nächste Termin ist AUSGEBUCHT -->
      <template v-if="isBookedOut(nextEvent)">
        <div class="booked-out-container">
          <strong class="badge-booked-out">Ausgebucht:</strong>
          <span class="strike-through">{{ formatDate(nextEvent.startDate) }}, {{ nextEvent.startTime }} Uhr</span>
        </div>
        
        <!-- Wenn vorhanden, zeige direkt den übernächsten Termin an -->
        <template v-if="alternativeEvent">
          <hr class="divider" />
          <p class="next-chance-text">✨ Nächste freie Chance:</p>
          <strong>Termin: </strong><span>{{ formatDate(alternativeEvent.startDate) }}, {{ alternativeEvent.startTime }} Uhr</span><br />
          <strong>Ort: </strong><span>{{ alternativeEvent.location }}</span><br />
          <strong>Dauer: </strong><span>ca. {{ getDurationText(alternativeEvent.startTime, alternativeEvent.endTime) }}</span><br />
        </template>
        
        <template v-else>
          <br /><span>Für diesen Kurs ist aktuell kein weiterer Termin geplant. Frag gerne unverbindlich an!</span><br />
        </template>
      </template>

      <!-- FALL 2: Ganz normaler nächster Termin (nicht ausgebucht) -->
      <template v-else>
        <strong>Nächster Termin: </strong>
        <span>{{ formatDate(nextEvent.startDate) }}, {{ nextEvent.startTime }} Uhr</span><br />
        <strong>Ort: </strong><span>{{ nextEvent.location }}</span><br />
        <strong>Dauer: </strong><span>ca. {{ getDurationText(nextEvent.startTime, nextEvent.endTime) }}</span><br />
      </template>

      <!-- Gemeinsame Infos für beide Fälle -->
      <strong>Kosten: </strong><span><router-link to="/preise/">Solidarisches Preismodell</router-link></span><br />
      <strong>Extras: </strong>
      <span>Ihr erhaltet im Anschluss ein ausführliches Handout.</span><br /><br />
    </template>

    <template v-else>
      <strong>Aktuell kein Termin geplant.</strong><br />Falls du Interesse hast, melde dich gerne bei mir!
      <strong>Ort: </strong><span>Koblenz-Metternich oder Online</span><br />
      <strong>Dauer: </strong><span>ca. 2 Stunden</span><br />
      <strong>Kosten: </strong><span><router-link to="/preise/">Solidarisches Preismodell</router-link></span><br />
      <strong>Extras: </strong>
      <span>Ihr erhaltet im Anschluss ein ausführliches Handout.</span><br /><br />
    </template>

    <!-- Der Button linkt immer auf das nächste FREIE Event oder auf die Anfrage -->
    <a :href="mailtoLink" class="cta-button" :class="{ 'secondary-btn': isBookedOut(nextEvent) && !alternativeEvent }">
      {{ ctaButtonText }}
    </a>
    <span><br /><br /></span>
  </div>
</template>

<script>
import events from "@/data/events-static.json";

export default {
  name: "NextEventBubble",
  data() {
    return {
      infoVisibleId: null,
      defaultOrganizer: {
        "@type": "Organization",
        name: "Maikes Familienbegleitung",
        url: "https://maikes-familienbegleitung.de/"
      },
      defaultPerformer: {
        name: "Maike Röhrig"
      },
      defaultDescription: "Ein interaktiver Kurs mit praktischen Tipps und individueller Beratung.",
      defaultImage: "https://maikes-familienbegleitung.de/favicon.svg",
      defaultPrice: "30",
      defaultOfferUrl: "https://maikes-familienbegleitung.de/preise/",
      defaultAddress: {
        streetAddress: "Trierer Strasse 250a",
        addressLocality: "Koblenz",
        postalCode: "56072",
        addressCountry: "DE"
      }
    };
  },
  mounted() {
    if (this.nextEvent != null) {
      const event = this.nextEvent;
      
      // SEO-Optimierung: Wenn ausgebucht, an Google melden
      const availabilitySchema = this.isBookedOut(event) 
        ? "https://schema.org/SoldOut" 
        : "https://schema.org/InStock";

      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Event",
        "name": event.name,
        "description": event.description || this.defaultDescription,
        "image": event.image || this.defaultImage,
        "startDate": `${event.startDate}T${event.startTime}:00+02:00`,
        "endDate": `${event.endDate}T${event.endTime}:00+02:00`,
        "location": {
              "@type": "Place",
              "name": event.location,
              "address": event.address || this.defaultAddress
            },
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "eventStatus": "https://schema.org/EventScheduled",
        "offers": {
          "@type": "Offer",
          "url": event.eventUrlRelative
            ? "https://maikes-familienbegleitung.de" + event.eventUrlRelative
            : event.eventUrlExtern
                ? event.eventUrlExtern
                : this.defaultOfferUrl,
          "price": event.price || this.defaultPrice,
          "priceCurrency": "EUR",
          "availability": availabilitySchema
        },
        "organizer": event.organizer || this.defaultOrganizer,
        "performer": event.performer
          ? { "@type": "Person", "name": event.performer }
          : { "@type": "Person", "name": this.defaultPerformer }
      }, null, 2);
      document.head.appendChild(script);
    }
  },
  computed: {
    // Gibt die nächsten zwei relevanten Events zurück (falls vorhanden)
    upcomingEvents() {
      const now = new Date();
      const currentPath = this.$route.path;
      return events
        .filter(e => {
          const eventDateTime = new Date(`${e.endDate}T${e.endTime}`);
          return e.eventUrlRelative === currentPath && eventDateTime >= now;
        })
        .sort((a, b) => a.startDate.localeCompare(b.startDate));
    },
    nextEvent() {
      return this.upcomingEvents[0] || null;
    },
    alternativeEvent() {
      // Gibt es einen zweiten Termin, wenn der erste ausgebucht ist?
      return this.upcomingEvents[1] || null;
    },
    ctaButtonText() {
      if (!this.nextEvent || (this.isBookedOut(this.nextEvent) && !this.alternativeEvent)) return "Unverbindlich anfragen";
      return "Jetzt anmelden";
    },
    mailtoLink() {
      const email = "info@maikes-familienbegleitung.de";

      // Bestimme, für welches Event die Anmeldung gilt (bevorzugt das freie alternative Event)
      const targetEvent = (this.nextEvent && this.isBookedOut(this.nextEvent)) 
        ? this.alternativeEvent 
        : this.nextEvent;

      if (targetEvent) {
        const subject = `Anmeldung ${targetEvent.name} ${this.formatDate(targetEvent.startDate)}`;
        const body = `Liebe Maike,%0D%0A%0D%0Aich möchte mich gerne zum ${targetEvent.name} am ${this.formatDate(targetEvent.startDate)} um ${targetEvent.startTime} Uhr anmelden.%0D%0A%0D%0AViele Grüße%0D%0A[Name]%0D%0A[Rechnungsadresse]`;
        return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${body}`;
      } else {
        const subject = `Anfrage zu Workshop-Termin`;
        const body = `Liebe Maike,%0D%0A%0D%0Aich interessiere mich für einen Workshop zum Thema [Stillen/Schlafen/Beikost]. Bitte gib mir Bescheid, sobald ein neuer Termin geplant ist.%0D%0A%0D%0AViele Grüße`;
        return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${body}`;
      }
    }
  },
  methods: {
    isBookedOut(event) {
      return event && event.badge && event.badge.toLowerCase() === "ausgebucht";
    },
    formatDate(isoDate) {
      const date = new Date(isoDate);
      return date.toLocaleDateString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    },
    getDurationText(start, end) {
      const [startHour, startMinute] = start.split(":").map(Number);
      const [endHour, endMinute] = end.split(":").map(Number);
      const startDate = new Date(0, 0, 0, startHour, startMinute);
      const endDate = new Date(0, 0, 0, endHour, endMinute);
      let diffMs = endDate - startDate;
      if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
      const diffHours = diffMs / (1000 * 60 * 60);
      return `${diffHours % 1 === 0 ? diffHours : diffHours.toFixed(1)} Stunden`;
    }
  }
};
</script>

<style scoped>
.event-bubble {
  position: absolute;
  top: 14rem;
  right: 1rem;
  background-color: var(--main-color-2);
  color: var(--font-color);
  padding: 10px 15px;
  border-radius: 12px;
  font-size: 0.9rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  max-width: 20rem;
}

/* Neue Styles für den Ausgebucht-Status */
.booked-out-container {
  margin-bottom: 0.5rem;
}
.badge-booked-out {
  color: #d32f2f; /* Ein dezentes Rot, passt meistens im Kontrast */
  background-color: #ffebee;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 5px;
}
.strike-through {
  text-decoration: line-through;
  opacity: 0.6;
}
.divider {
  border: 0;
  border-top: 1px dashed rgba(0, 0, 0, 0.15);
  margin: 10px 0;
}
.next-chance-text {
  margin: 0 0 5px 0;
  font-weight: bold;
}

@media (max-width: 70rem) {
  .event-bubble {
    position: relative;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>